import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IFactura, IFacturaForm, IObtenerFacturasOptions, IPaginacionFactura } from '../models/factura.interface';
import { switchMap, takeUntil, throttleTime } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';
import { FacturaService } from '../servicios/factura.service';

@Component({
  selector: 'app-documento-equivalente',
  templateUrl: './documento-equivalente.component.html',
  styleUrls: ['./documento-equivalente.component.css'],
})
export class DocumentoEquivalenteComponent implements OnInit, OnDestroy {
  public facturaForm!: FormGroup;
  private readonly EXPRESION_REGULAR_PLACA: string = '^(?=.*[a-zA-Z])(?=.*[0-9])[a-zA-Z0-9]+$';
  showAlert: boolean = false;
  showAlertInfo: boolean = false;
  showAlertError: boolean = false;
  private alertTrigger$ = new Subject<void>();
  private alertTriggerInfo$ = new Subject<void>();
  private alertTriggerError$ = new Subject<void>();
  private consultaSubject$ = new Subject<IFacturaForm>();
  private onDestroy$ = new Subject<void>();
  private consulta!: IObtenerFacturasOptions;
  arrayPaginadoResaltar: boolean[] = [];
  ultimaPaginaConsultada: number = 0;
  loadingServiceFactura: boolean = false;
  facturas: IFactura[] = [];
  rangoPaginasArray: number[] = [];
  paginaActual = 0;
  totalPaginas = 0;
  totalElementos: number = 0;
  ocultarPaginado: boolean = false;

  constructor(private formBuilder: FormBuilder, private facturaService: FacturaService) {
    this.inicializarForm();
  }

  ngOnInit(): void {
    this.alertTrigger$
      .pipe(
        switchMap(() => {
          this.showAlert = true;
          return timer(3000);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.showAlert = false;
      });

    this.alertTriggerInfo$
      .pipe(
        switchMap(() => {
          this.showAlertInfo = true;
          return timer(3000);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.showAlertInfo = false;
      });

    this.alertTriggerError$
      .pipe(
        switchMap(() => {
          this.showAlertError = true;
          return timer(3000);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.showAlertError = false;
      });

    // Suscripción al Subject con debounceTime
    this.consultaSubject$.pipe(
      throttleTime(1500),
    ).subscribe((data: IFacturaForm) => {
      this.obtenerFacturas(data.origen, (data?.placa || ''), `${data?.noTiquete || ''}`, data?.fechaCreacion, 1, 10);
    });
  }

  private inicializarForm(): void {
    this.facturaForm = this.formBuilder.group({
      origen: ["peaje_neiva", []],
      placa: ["", [Validators.minLength(4), Validators.maxLength(10), Validators.pattern(this.EXPRESION_REGULAR_PLACA)]],
      noTiquete: ["", []],
      fechaCreacion: ["", [Validators.required]],
    });
  }

  get placa() {
    return this.facturaForm.get('placa');
  }

  get noTiquete() {
    return this.facturaForm.get('noTiquete');
  }

  get fecha() {
    return this.facturaForm.get('fechaCreacion');
  }


  getInvoice() {
    const data: IFacturaForm = this.facturaForm.value;
    this.facturaForm.value;

    if (
      ((this.placa?.valid && this.noTiquete?.valid) && ((this.placa.value !== '' && this.placa.value !== null) || (this.noTiquete.value !== '') && this.noTiquete.value !== null)) && this.fecha?.valid) {
      this.showAlert = false;
      this.consultaSubject$.next(data);
    } else {
      this.facturaForm.markAllAsTouched();
      this.fecha?.valid ? this.alertTrigger$.next() : '';
    }
  }

  /**
* Páginado
* @param value - Array de 1 valor con el valor de la página
*/
  actualizarEstadoPagina(value: string[] | number[]): void {
    if (this.ultimaPaginaConsultada !== (Number(value[0]) + 1)) {
      switch (value[0]) {
        case "1p": this.irPrimeraPagina();
          break;
        case "pa": this.decrementar();
          break;
        case "ps": this.incrementar();
          break;
        case "up": this.irUltimaPagina();
          break;
        default: this.obtenerFacturas(
          this.consulta.origen,
          this.consulta?.placa,
          this.consulta?.noTiquete,
          this.consulta?.fechaRecaudo,
          (Number(value[0]) + 1) || 1,
          this.consulta?.size,
        );
          break;
      }
    }
  }

  private irPrimeraPagina() {
    this.paginaActual = 1;
    this.obtenerFacturas(
      this.consulta.origen,
      this.consulta?.placa,
      this.consulta?.noTiquete,
      this.consulta?.fechaRecaudo,
      1,
      10,
    );
  }

  private decrementar() {
    this.paginaActual--;

    if (this.paginaActual < 0) {
      this.paginaActual = 0;
    }

    this.obtenerFacturas(
      this.consulta.origen,
      this.consulta.placa,
      this.consulta.noTiquete,
      this.consulta.fechaRecaudo,
      this.paginaActual + 1,
      10,
    );
  }

  private incrementar() {
    this.paginaActual++;

    if (this.paginaActual > this.totalPaginas) {
      this.paginaActual = this.totalPaginas;
    }

    this.obtenerFacturas(
      this.consulta.origen,
      this.consulta.placa,
      this.consulta.noTiquete,
      this.consulta.fechaRecaudo,
      this.paginaActual + 1,
      10,
    );
  }

  private irUltimaPagina() {
    this.paginaActual = this.totalPaginas + 1;

    this.obtenerFacturas(
      this.consulta.origen,
      this.consulta.placa,
      this.consulta.noTiquete,
      this.consulta.fechaRecaudo,
      this.paginaActual,
      10,
    );
  }

  private obtenerFacturas(
    origen: 'peaje_neiva' | 'peaje_pata' | 'peaje_flandes',
    placa?: string,
    noTiquete?: string,
    fechaRecaudo?: string,
    page: number = 1,
    size: number = 10,
    sort: string = 'fechaCreacion;desc'
  ) {
    this.consulta = {
      origen: origen,
      placa: placa,
      noTiquete: noTiquete,
      fechaRecaudo: fechaRecaudo,
      page: page,
      size: size,
      sort: sort,
    };
    this.ultimaPaginaConsultada = page;
    this.loadingServiceFactura = true;
    this.facturaService.obtenerListaFacturas({ ...this.consulta, page: page, size: size }).subscribe({
      next: (facturas: IPaginacionFactura) => {
        this.facturas = facturas.content;

        this.facturas.length === 0 ? this.alertTriggerInfo$.next() : this.showAlertInfo = false;
        this.totalElementos = facturas.totalElements;

        this.totalPaginas = facturas?.totalPages - 1;
        this.paginaActual = facturas.pageable.pageNumber;

        this.ocultarPaginado = facturas?.totalElements <= 10;

        this.arrayPaginadoResaltar = [];
        this.arrayPaginadoResaltar.push(false);
        this.arrayPaginadoResaltar.push(false);

        this.rangoPaginasArray = this.calcularRangoPaginas(this.paginaActual, this.totalPaginas);
        this.rangoPaginasArray.forEach((item) => {
          const valueEnable: boolean = item === this.paginaActual
          this.arrayPaginadoResaltar.push(valueEnable);
        });

        this.arrayPaginadoResaltar.push(false);
        this.arrayPaginadoResaltar.push(false);

        this.loadingServiceFactura = false;
      },
      error: (e: Error) => {
        console.error('Error obteniendo facturas ' + e.message);
        this.alertTriggerError$.next();
        this.loadingServiceFactura = false;
      }
    });
  }

  private calcularRangoPaginas(paginaActual: number, totalPaginas: number) {
    const rangoPaginas = [];

    let inicio = paginaActual - 2;
    let fin = paginaActual + 2;

    if (inicio < 0) {
      inicio = 0;
      fin = 3;
    }

    if (fin > totalPaginas) {
      fin = totalPaginas;
      inicio = fin - 4;
    }

    for (let i = inicio; i <= fin; i++) {
      if (i >= 0) {
        rangoPaginas.push(i);
      }
    }
    return rangoPaginas;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  infoButt = {
    buttClass: 'btn-form btn-primary back',
    buttType: 'submit',
    buttValue: 'Consultar',
  };


  inputComponent = [
    {
      label: 'Placa',
      inputType: 'text',
      inputId: 'placa',
      inputClass: 'form-control',
      formControlName: 'placa',
      inputPHolder: '',
      maxLength: 10,
    },
    {
      label: 'Número tiquete',
      inputType: 'number',
      inputId: 'ticket',
      inputClass: 'form-control',
      formControlName: 'ticket',
      inputPHolder: '',
      maxLength: 10,
    },
    {
      label: 'Fecha',
      inputType: 'date',
      inputId: 'fecha',
      inputClass: 'form-control',
      formControlName: 'fecha',
      inputPHolder: '',
    },
  ];

  BanerFirstTab = {
    title: 'Documento equivalente',
    class1: 'white-bold-font',
    title2:
      'Descargue aquí el documento equivalente correspondiente al uso del peaje',
    class2: 'white text-info',
    dataTitle2: '',
    additionalTittle: '',
    additionalData: '',
  };

  dataHeader = {
    classTitle: 'blue negrilla',
    class1: 'text-center',
    title: 'Consulta documento equivalente',
  };

  documento = {
    title: 'Factura',
    class1: 'blue-bold',
    title2:
      'Ponemos a tu disposición esta herramienta para que puedas descargar tus facturas de manera fácil y rápida',
    selector: 'not',
  };
}
